import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, PlayCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { StatusPill, type Status } from '@/components/StatusPill';
import { ApiError, get } from '@/lib/api';

/**
 * O **botão TESTE**.
 *
 * Verifica o ambiente implantado, que a suíte de testes não alcança: migration
 * não aplicada, RLS desligada, extensão do Postgres ausente, Redis fora,
 * outbox acumulando, relógio fora de sincronia.
 */

interface SelfTestCheck {
  id: string;
  group: string;
  title: string;
  status: Status;
  detail?: string;
  durationMs: number;
}

interface SelfTestReport {
  status: Status;
  startedAt: string;
  durationMs: number;
  summary: { pass: number; warn: number; fail: number };
  checks: SelfTestCheck[];
}

const ICONS: Record<Status, typeof CheckCircle2> = {
  PASS: CheckCircle2,
  WARN: AlertTriangle,
  FAIL: XCircle,
  RUNNING: PlayCircle,
};

const ICON_TONE: Record<Status, string> = {
  PASS: 'text-success',
  WARN: 'text-warning',
  FAIL: 'text-destructive',
  RUNNING: 'text-muted-foreground',
};

export function DiagnosticsPage() {
  const run = useMutation<SelfTestReport, Error>({
    mutationFn: async () => {
      try {
        return await get<SelfTestReport>('/admin/selftest');
      } catch (error) {
        // 503 é resultado legítimo: o autoteste responde 503 quando reprova,
        // para que monitoração externa consuma o mesmo endpoint. O corpo vem
        // junto, então tratar como erro perderia o relatório.
        if (error instanceof ApiError && error.status === 503) {
          throw new Error(
            'O autoteste reprovou. Recarregue para ver o detalhe ou consulte o log.',
          );
        }
        throw error;
      }
    },
  });

  const report = run.data;
  const groups = report ? groupChecks(report.checks) : [];

  return (
    <div className="mx-auto max-w-[1400px] space-y-3">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">Diagnóstico</h1>
      </header>

      <Card>
        <CardHeader
          title="Autoteste do sistema"
          description="Nenhuma verificação escreve dado de negócio. Pode rodar quando quiser."
          action={
            <Button
              onClick={() => run.mutate()}
              loading={run.isPending}
              icon={<PlayCircle className="h-4 w-4" aria-hidden />}
              size="lg"
            >
              {run.isPending ? 'Verificando' : 'Testar'}
            </Button>
          }
        />

        {/*
          Um único `CardBody` sempre no mesmo lugar da árvore. Antes eram três
          irmãos trocando por ternário aninhado, o que faz o React remontar
          nós em vez de atualizá-los.
        */}
        <CardBody className="space-y-5">
          {run.isError ? <p className="text-sm text-destructive">{run.error.message}</p> : null}

          {!report && !run.isError ? (
            <p className="text-sm text-muted-foreground">Nenhuma verificação executada ainda.</p>
          ) : null}

          {report ? (
            <div className="animate-slide-up space-y-5">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <StatusPill status={report.status} />
              <span className="text-muted-foreground">
                <strong className="font-semibold text-foreground">{report.summary.pass}</strong> ok
                {' · '}
                <strong className="font-semibold text-foreground">{report.summary.warn}</strong>{' '}
                atenção
                {' · '}
                <strong className="font-semibold text-foreground">{report.summary.fail}</strong>{' '}
                falha
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {report.durationMs} ms
              </span>
            </div>

            {groups.map(([group, checks]) => (
              <section key={group} className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {group}
                </h3>
                <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
                  {checks.map((check) => {
                    const Icon = ICONS[check.status];
                    return (
                      <li key={check.id} className="flex items-start gap-3 bg-background px-4 py-3">
                        <Icon
                          className={`mt-0.5 h-4 w-4 flex-none ${ICON_TONE[check.status]}`}
                          aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="text-sm font-medium">{check.title}</span>
                            <span className="font-mono text-[11px] text-muted-foreground">
                              {check.durationMs} ms
                            </span>
                          </div>
                          {check.detail ? (
                            <p
                              className={`mt-0.5 break-words text-xs ${
                                check.status === 'PASS'
                                  ? 'text-muted-foreground'
                                  : ICON_TONE[check.status]
                              }`}
                            >
                              {check.detail}
                            </p>
                          ) : null}
                        </div>
                        <span className="sr-only">{check.status}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

/** Mantém a ordem em que a API devolveu — ela reflete a ordem de execução. */
function groupChecks(checks: SelfTestCheck[]): Array<[string, SelfTestCheck[]]> {
  const groups = new Map<string, SelfTestCheck[]>();
  for (const check of checks) {
    const list = groups.get(check.group) ?? [];
    list.push(check);
    groups.set(check.group, list);
  }
  return [...groups.entries()];
}
